import React, { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Trash2, Plus, Save, BarChart3, ListPlus } from "lucide-react";
import { Navigate } from "react-router-dom";

const SLOTS = ["wardrobe", "outfit", "discover"];

const empty = { title: "", brand: "", image_url: "", target_url: "", slot: "wardrobe", category: "", active: true };

const Admin = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("placements"); // placements | analytics
  const [ads, setAds] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const load = async () => {
    try {
      const [ads_r, an_r] = await Promise.all([
        api.get("/admin/ads"),
        api.get("/admin/analytics"),
      ]);
      setAds(ads_r.data);
      setAnalytics(an_r.data);
    } catch (e) { if (e?.response?.status === 403) setForbidden(true); }
  };
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line

  if (!user) return <Navigate to="/auth" replace/>;
  if (forbidden) return <div className="p-20 text-center font-serif-display text-3xl">Members-only area — you're not on the list.</div>;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/ads/${editing}`, form);
        toast.success("Ad updated");
      } else {
        await api.post("/admin/ads", form);
        toast.success("Ad added");
      }
      setForm(empty); setEditing(null);
      load();
    } catch (err) { toast.error(errText(err, "Save failed")); }
    finally { setBusy(false); }
  };

  const edit = (a) => { setForm({ title: a.title, brand: a.brand, image_url: a.image_url, target_url: a.target_url, slot: a.slot, category: a.category || "", active: a.active }); setEditing(a.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { if (!confirm("Delete this ad?")) return; await api.delete(`/admin/ads/${id}`); load(); };

  return (
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
      <div className="border-b border-black/10 pb-6 mb-6 flex items-baseline justify-between">
        <div>
          <div className="font-mono-eyebrow">Admin · Affiliate placements</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none mt-2">Ads Desk</h1>
        </div>
        <div className="font-mono-eyebrow text-black/50">{ads.length} placements</div>
      </div>

      <div className="flex items-center gap-2 mb-10">
        <button data-testid="admin-tab-placements" onClick={()=>setTab("placements")}
          className={`px-4 py-2 border font-mono-eyebrow flex items-center gap-2 ${tab==="placements" ? "bg-black text-[#F6F5F1] border-black" : "border-black/20 hover:border-black"}`}>
          <ListPlus className="w-3.5 h-3.5"/> Placements
        </button>
        <button data-testid="admin-tab-analytics" onClick={()=>setTab("analytics")}
          className={`px-4 py-2 border font-mono-eyebrow flex items-center gap-2 ${tab==="analytics" ? "bg-black text-[#F6F5F1] border-black" : "border-black/20 hover:border-black"}`}>
          <BarChart3 className="w-3.5 h-3.5"/> Analytics
        </button>
      </div>

      {tab === "analytics" ? (
        <AdminAnalytics analytics={analytics}/>
      ) : (
      <div className="grid grid-cols-12 gap-10">
        <form onSubmit={submit} className="col-span-12 lg:col-span-5 border border-black/10 bg-white p-8 space-y-6 self-start md:sticky md:top-32">
          <div className="font-mono-eyebrow">{editing ? "Edit placement" : "New placement"}</div>
          <div><label className="font-mono-eyebrow">Title</label><input required data-testid="ad-title" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} className="field mt-1"/></div>
          <div><label className="font-mono-eyebrow">Brand</label><input required data-testid="ad-brand" value={form.brand} onChange={(e)=>setForm({...form, brand: e.target.value})} className="field mt-1"/></div>
          <div><label className="font-mono-eyebrow">Image URL</label><input required data-testid="ad-image" value={form.image_url} onChange={(e)=>setForm({...form, image_url: e.target.value})} className="field mt-1" placeholder="https://…"/></div>
          <div><label className="font-mono-eyebrow">Affiliate Target URL</label><input required data-testid="ad-target" value={form.target_url} onChange={(e)=>setForm({...form, target_url: e.target.value})} className="field mt-1" placeholder="https://…?tag=affiliate"/></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono-eyebrow">Slot</label>
              <div className="flex gap-2 mt-2">
                {SLOTS.map(s => (
                  <button type="button" key={s} data-testid={`ad-slot-${s}`}
                    onClick={()=>setForm({...form, slot: s})}
                    className={`px-3 py-1.5 border font-mono-eyebrow ${form.slot===s?"bg-black text-[#F6F5F1] border-black":"border-black/20 hover:border-black"}`}>{s}</button>
                ))}
              </div>
            </div>
            <div><label className="font-mono-eyebrow">Category</label><input data-testid="ad-category" value={form.category} onChange={(e)=>setForm({...form, category: e.target.value})} className="field mt-1" placeholder="e.g. top"/></div>
          </div>
          <label className="flex gap-3 items-center cursor-pointer">
            <input type="checkbox" data-testid="ad-active" checked={form.active} onChange={(e)=>setForm({...form, active: e.target.checked})} className="w-4 h-4 accent-black"/>
            <span className="font-mono-eyebrow">Active</span>
          </label>
          <div className="flex gap-3 pt-4 border-t border-black/10">
            {editing && <button type="button" onClick={()=>{ setEditing(null); setForm(empty); }} className="btn-outline-ink">Cancel</button>}
            <button type="submit" data-testid="ad-save" disabled={busy} className="btn-ink flex items-center gap-2 disabled:opacity-50">
              {editing ? <Save className="w-3.5 h-3.5"/> : <Plus className="w-3.5 h-3.5"/>} {editing ? "Update" : "Create"}
            </button>
          </div>
        </form>

        <div className="col-span-12 lg:col-span-7">
          {SLOTS.map(slot => {
            const slotAds = ads.filter(a => a.slot === slot);
            return (
              <section key={slot} className="mb-10">
                <h2 className="font-serif-display text-3xl capitalize mb-4">{slot} slot <span className="font-mono-eyebrow text-black/40 ml-3">{slotAds.length}</span></h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {slotAds.map(a => (
                    <div key={a.id} className="bg-white border border-black/10">
                      <div className="aspect-[3/4] overflow-hidden"><img src={a.image_url} alt="" className="w-full h-full object-cover"/></div>
                      <div className="p-3">
                        <div className="font-mono-eyebrow text-black/50">{a.brand}</div>
                        <div className="font-serif-display text-lg leading-tight line-clamp-2">{a.title}</div>
                        <div className="mt-2 flex items-center justify-between">
                          <span className={`font-mono-eyebrow ${a.active?"text-[color:var(--accent)]":"text-black/40"}`}>{a.active?"Live":"Off"}</span>
                          <div className="flex gap-2">
                            <button data-testid={`ad-edit-${a.id}`} onClick={()=>edit(a)} className="editorial-link font-mono-eyebrow">Edit</button>
                            <button data-testid={`ad-del-${a.id}`} onClick={()=>remove(a.id)} className="text-black/40 hover:text-red-700"><Trash2 className="w-3.5 h-3.5"/></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {slotAds.length === 0 && <div className="col-span-3 text-black/40 font-mono-eyebrow py-6">No placements yet</div>}
                </div>
              </section>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
};

const AdminAnalytics = ({ analytics }) => {
  if (!analytics) return <div className="font-mono-eyebrow text-black/50 py-10">Loading analytics…</div>;
  const { rows, totals, by_slot } = analytics;
  const sorted = [...rows].sort((a,b) => (b.clicks - a.clicks) || (b.impressions - a.impressions));
  const bestCtr = [...rows].filter(r => r.impressions > 0).sort((a,b) => b.ctr - a.ctr)[0];
  return (
    <div data-testid="analytics-panel">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <div className="border border-black/10 bg-white p-6">
          <div className="font-mono-eyebrow text-black/50">Total impressions</div>
          <div className="font-serif-display text-5xl mt-1" data-testid="kpi-impressions">{totals.impressions.toLocaleString()}</div>
        </div>
        <div className="border border-black/10 bg-white p-6">
          <div className="font-mono-eyebrow text-black/50">Total clicks</div>
          <div className="font-serif-display text-5xl mt-1" data-testid="kpi-clicks">{totals.clicks.toLocaleString()}</div>
        </div>
        <div className="border border-black/10 bg-white p-6">
          <div className="font-mono-eyebrow text-black/50">Overall CTR</div>
          <div className="font-serif-display text-5xl mt-1" data-testid="kpi-ctr">{totals.ctr}%</div>
        </div>
        <div className="border border-[color:var(--accent)] bg-white p-6">
          <div className="font-mono-eyebrow text-[color:var(--accent)]">Best performer</div>
          <div className="font-serif-display text-xl leading-tight mt-1 line-clamp-2">
            {bestCtr ? `${bestCtr.brand} · ${bestCtr.title}` : "—"}
          </div>
          <div className="font-mono-eyebrow text-black/50 mt-1">{bestCtr ? `${bestCtr.ctr}% CTR` : "no data yet"}</div>
        </div>
      </div>

      {/* By slot */}
      <div className="grid grid-cols-3 gap-4 mb-10">
        {Object.entries(by_slot).map(([slot, v]) => (
          <div key={slot} className="border border-black/10 bg-[#F1EFE7] p-5">
            <div className="font-mono-eyebrow capitalize">{slot}</div>
            <div className="mt-2 flex items-baseline gap-4">
              <div><div className="font-mono-eyebrow text-black/50 text-[10px]">Impr.</div><div className="font-serif-display text-2xl">{v.impressions}</div></div>
              <div><div className="font-mono-eyebrow text-black/50 text-[10px]">Clicks</div><div className="font-serif-display text-2xl">{v.clicks}</div></div>
              <div><div className="font-mono-eyebrow text-black/50 text-[10px]">CTR</div><div className="font-serif-display text-2xl">{v.impressions ? ((v.clicks/v.impressions)*100).toFixed(1) : "0"}%</div></div>
            </div>
          </div>
        ))}
      </div>

      {/* Per-ad table */}
      <div className="border border-black/10 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-black/10">
            <tr className="text-left">
              <th className="p-4 font-mono-eyebrow">Placement</th>
              <th className="p-4 font-mono-eyebrow">Slot</th>
              <th className="p-4 font-mono-eyebrow text-right">Impressions</th>
              <th className="p-4 font-mono-eyebrow text-right">Clicks</th>
              <th className="p-4 font-mono-eyebrow text-right">CTR</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.id} className="border-b border-black/5" data-testid={`analytics-row-${r.id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-12 bg-neutral-100 overflow-hidden shrink-0">
                      <img src={r.image_url} alt="" className="w-full h-full object-cover"/>
                    </div>
                    <div>
                      <div className="font-mono-eyebrow text-black/50">{r.brand}</div>
                      <div className="font-serif-display text-base leading-tight">{r.title}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 font-mono-eyebrow capitalize">{r.slot}</td>
                <td className="p-4 text-right font-mono-eyebrow">{r.impressions}</td>
                <td className="p-4 text-right font-mono-eyebrow">{r.clicks}</td>
                <td className="p-4 text-right font-mono-eyebrow">{r.ctr}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Admin;
